import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { ChevronDown, ListChecks } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useSeriesChecklist } from './use-series-checklist';

/**
 * « Points à aborder » — outil du TUTEUR pendant la visio : il choisit une série
 * (créée dans Rendez-vous → Séries) et coche les points au fil de l'échange.
 * L'élève ne le voit jamais (rendu réservé au tuteur par `RoomPage`, et la RLS
 * n'ouvre ces tables qu'au tuteur). La série choisie devient la série proposée
 * par défaut à la génération du résumé.
 */
export function SeriesChecklist({ db, roomId }: { db: SupabaseClient; roomId: string }) {
  const { loading, error, series, active, checkedIds, selectSeries, toggle } = useSeriesChecklist(
    db,
    roomId,
  );
  const [open, setOpen] = useState(false);

  // Une série déjà choisie pour cette salle (rechargement de la page) : on l'ouvre d'emblée.
  const initialised = useRef(false);
  useEffect(() => {
    if (loading || initialised.current) return;
    initialised.current = true;
    if (active) setOpen(true);
  }, [loading, active]);

  const done = active ? active.points.filter((p) => checkedIds.includes(p.id)).length : 0;

  return (
    <section aria-label="Points à aborder" className="border-ink-100 rounded-2xl border bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[2.75rem] w-full items-center gap-2 px-3 py-2 text-left active:scale-[0.99]"
      >
        <ListChecks className="text-primary-600 size-4 shrink-0" />
        <span className="text-ink-900 min-w-0 flex-1 truncate text-sm font-semibold">
          Points à aborder{active ? ` · ${active.title}` : ''}
        </span>
        {active && (
          <span className="text-ink-500 shrink-0 text-xs font-semibold">
            {done}/{active.points.length}
          </span>
        )}
        <ChevronDown
          className={cn('text-ink-400 size-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="border-ink-100 space-y-3 border-t p-3">
          {loading ? (
            <p className="text-ink-500 text-sm">Chargement…</p>
          ) : series.length === 0 ? (
            <p className="text-ink-500 text-sm">
              Tu n’as pas encore de série de points. Crée-en dans « Rendez-vous → Séries » sur
              JeRaisonne.
            </p>
          ) : (
            <>
              <select
                aria-label="Série de points"
                value={active?.id ?? ''}
                onChange={(e) => selectSeries(e.target.value || null)}
                className="border-ink-200 text-ink-900 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-base focus:outline-none sm:text-sm"
              >
                <option value="">Choisir une série…</option>
                {series.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>

              {active && (
                <ul className="max-h-[40dvh] space-y-1 overflow-y-auto">
                  {active.points.map((point) => {
                    const checked = checkedIds.includes(point.id);
                    return (
                      <li key={point.id}>
                        <label className="hover:bg-ink-50 flex min-h-[2.75rem] cursor-pointer items-start gap-3 rounded-lg px-2 py-2.5 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(point.id)}
                            className="accent-primary-600 mt-0.5 size-5 shrink-0"
                          />
                          <span
                            className={cn(checked ? 'text-ink-400 line-through' : 'text-ink-800')}
                          >
                            {point.text}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
          {error && (
            <p role="alert" className="text-accent-700 text-xs font-medium">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
